import Form from "@/components/Form";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useProjectSchedulesContext } from "@/modules/projects/components/schedules/ProjectSchedulesContext";

import { memo, useCallback, useEffect } from "react";
import { useRowSelection } from "@/components/data-table/hooks/useRowSelection";
import { ProjectSchedulesDialogHeader } from "@/modules/projects/components/schedules/ProjectSchedulesDialogHeader";
import { ProjectSchedulesDeleteDialogForm } from "@/modules/projects/components/schedules/ProjectSchedulesDeleteDialogForm";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import { ProjectSchedulesDeleteDialogFooter } from "./ProjectSchedulesDeleteDialogFooter";
import { ProjectSchedulesDeleteFormData } from "@/types/projectSchedulesDeleteForm";
import { useFormatters } from "@/lib/hooks/useFormatters";
import {
  AsyncOperationProgress,
  OperationProgressStatus,
} from "@/components/progress/AsyncOperationProgress";
import { useProjectSchedulesDeleteFormWithBatch } from "@/modules/projects/hooks/useProjectSchedulesDeleteFormWithBatch";

const DELETE_BATCH_SIZE = 100;

const ProjectSchedulesDeleteDialogComponent = () => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const { formatDate } = useFormatters();
  const {
    projectId,
    refetch,
    selectedStartDate: dateStart,
    selectedEndDate: dateEnd,
    isDeleteDialogOpened,
    setIsDeleteDialogOpened,
  } = useProjectSchedulesContext();
  const { getSelection, clear } = useRowSelection();

  const onSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  const { form, defaultValues, mutation, operationProgress, cancel } =
    useProjectSchedulesDeleteFormWithBatch({
      projectId,
      dateStart: formatDate(dateStart),
      dateEnd: formatDate(dateEnd),
      getSelection,
      onSuccess,
      batchSize: DELETE_BATCH_SIZE,
    });

  useEffect(() => {
    form.setValue("date_start", formatDate(dateStart));
    form.setValue("date_end", formatDate(dateEnd));
  }, [form, dateStart, dateEnd, formatDate]);

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (!open && operationProgress.isBusy()) {
        return;
      }
      form.reset(defaultValues);
      operationProgress.reset();
      mutation.reset();
      setIsDeleteDialogOpened(open);
    },
    [defaultValues, form, mutation, operationProgress, setIsDeleteDialogOpened],
  );

  const handleClose = useCallback(() => {
    const hasFailures = operationProgress.items.some(
      (item) => item.errorDescription !== undefined,
    );
    const allSucceeded =
      operationProgress.items.length > 0 &&
      operationProgress.items.every((item) => item.success === true);

    operationProgress.reset();
    setIsDeleteDialogOpened(false);
    form.reset(defaultValues);
    clear();

    if (hasFailures) {
      toast({
        title: t("projectSchedules.error.schedulesNotDeleted"),
        variant: "destructive",
      });
    } else if (allSucceeded) {
      toast({
        title: t("projectSchedules.success.schedulesDeleted"),
      });
    }
  }, [
    clear,
    defaultValues,
    form,
    operationProgress,
    setIsDeleteDialogOpened,
    t,
    toast,
  ]);

  const handleSubmit = useCallback(
    (values: ProjectSchedulesDeleteFormData) => {
      mutation.mutate(values);
    },
    [mutation],
  );

  const isShowingProgress =
    operationProgress.status !== OperationProgressStatus.IDLE;

  return (
    <Dialog open={isDeleteDialogOpened} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[40dvw] min-w-[700px] max-h-[90dvh] overflow-y-auto border-2 border-destructive text-red-800">
        <ProjectSchedulesDialogHeader
          variant="destructive"
          title={t("projectSchedules.dialog.delete")}
        />
        {isShowingProgress ? (
          <AsyncOperationProgress
            operationProgress={operationProgress}
            currentItemLabel={operationProgress.currentItemLabel}
            translationKey="projectSchedules.deleteProgress"
            onCancel={cancel}
            onClose={handleClose}
          />
        ) : (
          <Form form={form} handleSubmit={handleSubmit} className="gap-6">
            <ProjectSchedulesDeleteDialogForm />
            <ProjectSchedulesDeleteDialogFooter disabled={mutation.isPending} />
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};
export const ProjectSchedulesDeleteDialog = memo(
  ProjectSchedulesDeleteDialogComponent,
);
